import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardBody, Spinner } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { tokenStorage } from '@/lib/token-storage';
import { brand } from '@/config/brand';
import styles from './AuthMessage.module.css';

/**
 * Impersonation entry (legacy Auth/Impersonation). A staff tool links here with
 * the impersonated session in the URL: `/auth/impersonation/:method#jwt=&exp=&uuid=`
 * (`method` = 'sso' | 'local', `exp` = absolute UNIX seconds). Clears any prior
 * session, persists the impersonated one, and hard-reloads into the app; shows an
 * expired notice if the token lapsed.
 *
 * Security: the session JWT is read from the URL **fragment** (`#…`), which
 * browsers never send to the server, so it can't land in access logs or a
 * `Referer` header. A legacy `?jwt=` query param is still accepted for backward
 * compat (the staff link generator should migrate to `#`), and either way the
 * token is scrubbed from the address bar on mount so it can't be copied,
 * bookmarked, or leaked.
 */
export default function ImpersonationPage() {
  const { method } = useParams<{ method: string }>();
  const [params] = useSearchParams();
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    // Prefer the fragment; fall back to the legacy query param.
    const fragment = new URLSearchParams(
      window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '',
    );
    const access = fragment.get('jwt') ?? params.get('jwt');
    const expSeconds = Number(fragment.get('exp') ?? params.get('exp'));
    const uuid = fragment.get('uuid') ?? params.get('uuid') ?? '';

    // DC impersonator identity (present only when a DataCentral employee started
    // this session). Lets the app flag it as a DC-driven impersonation and show
    // who is behind it, vs a direct customer login.
    const impByName = fragment.get('imp_by_name') ?? params.get('imp_by_name') ?? '';
    const impById = fragment.get('imp_by_id') ?? params.get('imp_by_id') ?? '';
    const impByEmail = fragment.get('imp_by_email') ?? params.get('imp_by_email') ?? '';
    const impersonatedBy =
      impByName || impById ? { id: impById, name: impByName, email: impByEmail } : undefined;
    // Encrypted token verified server-side (@mgmt) — the authoritative source.
    const impersonationToken =
      fragment.get('imp_token') ?? params.get('imp_token') ?? undefined;

    // Scrub the token from the URL immediately, regardless of how it arrived,
    // keeping the route path so this view still renders.
    if (window.location.search || window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!access || !expSeconds || !uuid || !method || expSeconds * 1000 <= Date.now()) {
      setExpired(true);
      return;
    }
    // Wipe the prior session BEFORE writing the impersonated one. Otherwise the
    // previous account leaks in: token keys get overwritten but stale identity
    // remains (the legacy bug — impersonate "succeeds" yet the app still shows
    // the original user).
    tokenStorage.clear();
    tokenStorage.set({
      access,
      expSeconds,
      uuid,
      authMethod: method === 'local' ? 'local' : 'sso',
      impersonatedBy,
      impersonationToken,
    });
    // Hard reload (not SPA navigate) so the previous user's in-memory React Query
    // cache and provider state are discarded and the app re-bootstraps from the
    // freshly written impersonation session.
    window.location.replace('/');
  }, [method, params]);

  return (
    <div className={styles.screen}>
      <div className={styles.box}>
        <img src={brand.assets.icon} width={48} height={48} alt={brand.shortName} className={styles.logo} />
        <Card>
          <CardBody>
            {expired ? (
              <div className={styles.content}>
                <h1 className={styles.title}>Your session has expired</h1>
                <Alert tone="danger">
                  This impersonation token is expired. Please close this window and start impersonation
                  again.
                </Alert>
                <p className={styles.muted}>
                  Still having trouble? Contact {brand.emails.support}.
                </p>
              </div>
            ) : (
              <div className={`${styles.content} ${styles.center}`}>
                <Spinner />
                <p className={styles.text}>Signing you in…</p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
